import { useMutation } from "react-query";
import { useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import { postApi } from "@circle-react/api";
import { useToast } from "@circle-react/components/shared/uikit/ToastV2";
import { ToggleSwitchWithLabel } from "@circle-react/components/shared/uikit/ToggleSwitchWithLabel";
import type { ApiError } from "@circle-react/config/CustomErrors";
import { usePostListPinnedOnSidebar } from "@circle-react/hooks/posts/usePostList";
import type { Post } from "@circle-react/types/Post";
import { Dropdown } from "@circle-react-uikit/Dropdown";

export interface PinToSidebarActionProps {
  handleClose?: () => void;
  onChange?: (postData: Post, options: { refetch: boolean }) => void;
  post: Post;
}

export const PinToSidebarAction = ({
  post,
  handleClose,
  onChange,
}: PinToSidebarActionProps) => {
  const isPinned = !!post?.pinned_at;
  const queryClient = useQueryClient();

  const { success, error } = useToast();
  const { refetch: refetchSideBarPosts } = usePostListPinnedOnSidebar();

  const handleOnClick = useMutation<Post, ApiError, void>(
    () =>
      postApi.pin({
        post,
        formData: { ...post, is_pinned: !isPinned, pin_to_top: false },
      }) as Promise<Post>,
    {
      onSuccess: async post => {
        onChange?.(post, { refetch: false });
        await refetchSideBarPosts();
        await queryClient.invalidateQueries("posts");

        success(
          t(isPinned ? "post_unpinned_from_sidebar" : "post_pinned_to_sidebar"),
        );
      },
      onError: () => {
        error(t(isPinned ? "post_unpinned_error" : "post_pinned_error"));
      },
      onSettled: handleClose,
    },
  );

  return (
    <Dropdown.ItemLink onClick={() => handleOnClick.mutate()}>
      <ToggleSwitchWithLabel
        label={t("pin_post")}
        field="pin_post"
        value={isPinned}
        variant="small"
      />
    </Dropdown.ItemLink>
  );
};
